/**
 * OpenAI Integration — the Design JSON V2 response schema.
 *
 * A JSON Schema describing the document the model must return, derived from the
 * frozen Phase 1 vocabulary so the schema, the prompt, and the validator can
 * never drift: adding a vocabulary value updates all three at once.
 *
 * It is written for the provider's STRICT structured-output mode, which makes
 * the model physically unable to emit a field the contract does not define.
 * Strict mode imposes three rules that shape everything below:
 *
 *   1. every object must set `additionalProperties: false`
 *   2. every property must appear in `required`
 *   3. "optional" is therefore expressed as a nullable union, not by omission
 *
 * Nullability mirrors Phase 1's factories exactly (a factory default of `null`
 * means the field is nullable; any other default means it is not). Fields that
 * belong to only one node kind are nullable because rule 2 forces every node to
 * carry every key — the Phase 2 validator remains the authority on whether a
 * given node uses them correctly.
 */

import {
  SCHEMA_ID, SCHEMA_VERSION, NODE_KINDS, PRIMITIVE_TYPES, SHAPE_KINDS, FILL_KINDS,
  GRADIENT_KINDS, EFFECT_KINDS, MASK_KINDS, LAYOUT_TYPES, SIZING_MODES, ALIGN_MODES,
  DISTRIBUTE_MODES, SECTION_ROLES, LAYER_BANDS, BLEND_MODES, STROKE_ALIGNS,
  STROKE_STYLES, TEXT_CASES, TEXT_ALIGNS, CONSTRAINT_H, CONSTRAINT_V, ORIENTATIONS,
  UNITS, FIT_MODES, COLOR_ROLES, TYPE_STYLES, SPACE_STEPS,
} from '../schema';

/* ------------------------------------------------------------- strict helpers */

const str = { type: 'string' };
const num = { type: 'number' };
const bool = { type: 'boolean' };
const strOrNull = { type: 'string' }; // omit when absent — never null
const numOrNull = { type: 'number' }; // omit when absent — never null
/** A space/radius token ref or a raw px value. */
const tokenOrNumber = { type: ['string', 'number'] };

const enumOf = (values) => ({ type: 'string', enum: [...values] });
const enumOrNull = (values) => ({ type: 'string', enum: [...values] }); // omit when absent — never null

/**
 * A closed object: nothing outside the contract is permitted.
 *
 * `required` lists ONLY fields that must always be present. Everything else is
 * OMITTED when it does not apply — never emitted as null. (An all-required
 * schema forces a model to fill inapplicable fields with null, which is exactly
 * what the validator rejects.)
 */
const object = (properties, required = [], description) => ({
  type: 'object',
  ...(description ? { description } : {}),
  properties,
  ...(required.length ? { required } : {}),
  additionalProperties: false,
});

const arrayOf = (items) => ({ type: 'array', items });
const ref = (name) => ({ $ref: `#/$defs/${name}` });
const refOrNull = (name) => ref(name); // omit when absent — never null

/**
 * A free-form map (component props/slots, extension token groups).
 *
 * Strict mode cannot express an open map — `additionalProperties: false` is
 * mandatory — so these are constrained to `{}` or null. That is a deliberate
 * trade: a closed contract the model cannot violate beats an open one it
 * invents fields into.
 */
const closedMap = { type: 'object', properties: {}, additionalProperties: false };

/**
 * The node kinds and primitives the model may emit — a deliberate SUBSET of the
 * frozen Phase 1 vocabulary. `component` is withheld until the component system
 * is complete; Design JSON V2 itself is unchanged.
 */
const ALLOWED_NODE_KINDS = NODE_KINDS.filter((k) => k !== 'component');
const ALLOWED_PRIMITIVE_TYPES = [...PRIMITIVE_TYPES];

const fromKeys = (keys, schema) => keys.reduce((acc, k) => ({ ...acc, [k]: schema }), {});

/* -------------------------------------------------------------------- $defs */

const $defs = {
  fill: object({
    kind: enumOf(FILL_KINDS),
    color: strOrNull,
    gradient: enumOrNull(GRADIENT_KINDS),
    stops: arrayOf(object({ color: str, at: num }, ['color', 'at'])),
    angle: numOrNull,
    src: strOrNull,
    query: strOrNull,
    fit: enumOrNull(FIT_MODES),
    pattern: strOrNull,
    opacity: numOrNull,
    blend: enumOrNull(BLEND_MODES),
  }, ['kind']),

  // Phase 1: createStroke() always returns an object; only its `fill` is nullable.
  stroke: object({
    width: num,
    align: enumOf(STROKE_ALIGNS),
    style: enumOf(STROKE_STYLES),
    fill: strOrNull,
  }, ['width']),

  effect: object({
    kind: enumOf(EFFECT_KINDS),
    color: strOrNull,
    x: numOrNull,
    y: numOrNull,
    blur: numOrNull,
    spread: numOrNull,
    opacity: numOrNull,
    amount: numOrNull,
    mode: enumOrNull(BLEND_MODES),
  }, ['kind']),

  mask: object({
    kind: enumOf(MASK_KINDS),
    shape: enumOrNull(SHAPE_KINDS),
    src: strOrNull,
    ref: strOrNull,
  }, ['kind']),

  constraints: object({ h: enumOf(CONSTRAINT_H), v: enumOf(CONSTRAINT_V) }, ['h', 'v']),

  editable: object({
    content: bool, style: bool, position: bool, size: bool, swappable: bool, locked: bool,
  }, []),

  grid: object({ cols: num, rows: numOrNull, gutter: tokenOrNumber, margin: tokenOrNumber }, ['cols']),

  autoLayout: object({
    type: enumOf(LAYOUT_TYPES),
    gap: tokenOrNumber,
    pad: tokenOrNumber,
    align: enumOf(ALIGN_MODES),
    distribute: enumOf(DISTRIBUTE_MODES),
    sizing: enumOf(SIZING_MODES),
    grid: refOrNull('grid'),
  }, ['type']),

  typeStyle: object({
    font: strOrNull,
    weight: num,
    step: num,
    leading: num,
    tracking: num,
    case: enumOf(TEXT_CASES),
  }, []),

  tokens: object({
    color: object(fromKeys(COLOR_ROLES, strOrNull)),
    type: object(fromKeys(TYPE_STYLES, ref('typeStyle'))),
    space: object(fromKeys(SPACE_STEPS, num)),
    radius: object(fromKeys(['none', 'sm', 'md', 'lg', 'pill'], num)),
    elevation: closedMap,
    stroke: closedMap,
    effect: closedMap,
  }, []),

  /**
   * One node schema covers every kind. Keys that do not apply to a kind are
   * simply OMITTED — never null.
   *
   * Geometry (x/y/width/height) is absent from this contract on purpose: the
   * Rendering Engine resolves all geometry, so a node that carries coordinates
   * is outside the contract entirely.
   *
   * `component` instances are excluded until the component system is complete.
   */
  node: object({
    id: strOrNull,
    node: enumOf(ALLOWED_NODE_KINDS),
    type: enumOrNull(ALLOWED_PRIMITIVE_TYPES),
    role: strOrNull,
    layer: enumOf(LAYER_BANDS),
    rotation: num,
    fills: arrayOf(ref('fill')),
    stroke: ref('stroke'),
    effects: arrayOf(ref('effect')),
    mask: refOrNull('mask'),
    constraints: ref('constraints'),
    editable: ref('editable'),
    // text
    text: strOrNull,
    style: enumOrNull(TYPE_STYLES),
    color: strOrNull,
    align: enumOrNull(TEXT_ALIGNS),
    case: enumOrNull(TEXT_CASES),
    // shape / vector
    shape: enumOrNull(SHAPE_KINDS),
    cornerRadius: tokenOrNumber,
    path: strOrNull,
    // image / icon
    // `src` stays in the contract because the model may legitimately reference
    // an asset the brief supplied. It must NOT invent one: the prompt forbids
    // it and `assetIntent` is how imagery is actually requested.
    src: strOrNull,
    query: strOrNull,
    assetIntent: refOrNull('assetIntent'),
    fit: enumOrNull(FIT_MODES),
    icon: strOrNull,
    // group
    layout: refOrNull('autoLayout'),
    children: arrayOf(ref('node')),
    // frame
    clip: enumOrNull(SHAPE_KINDS),
    child: refOrNull('node'),
  }, ['node']),

  /**
   * What photograph this node needs. The DESIGNER's decision; a stock library
   * resolves it to a real image. Closed, like every other object here, so the
   * model cannot invent adjacent fields the resolver would silently ignore.
   */
  assetIntent: object({
    subject: strOrNull,
    searchQuery: strOrNull,
    keywords: arrayOf({ type: 'string' }),
    orientation: enumOrNull(['landscape', 'portrait', 'square']),
    mood: arrayOf({ type: 'string' }),
    visualStyle: strOrNull,
    cropStrategy: strOrNull,
    placementIntent: strOrNull,
    aspectPreference: strOrNull,
    focalPoint: object({ x: numOrNull, y: numOrNull }),
    required: { type: 'boolean' },
    sourcePreference: enumOrNull(['pixabay', 'pexels', 'unsplash']),
  }, ['subject', 'searchQuery']),

  section: object({
    id: strOrNull,
    role: enumOf(SECTION_ROLES),
    layout: ref('autoLayout'),
    nodes: arrayOf(ref('node')),
  }, ['role', 'nodes']),

  canvas: object({
    width: num,
    height: num,
    unit: enumOf(UNITS),
    orientation: enumOf(ORIENTATIONS),
    bleed: num,
    safeArea: { type: 'number', description: 'Inset as a FRACTION of the canvas, 0 to 0.5 (e.g. 0.06). Never a pixel or millimetre value.' },
    grid: ref('grid'),
  }, ['width', 'height']),

  page: object({
    id: strOrNull,
    canvas: ref('canvas'),
    background: arrayOf(ref('fill')),
    layers: arrayOf(enumOf(LAYER_BANDS)),
    sections: arrayOf(ref('section')),
  }, ['canvas', 'sections']),

  variant: object({ size: strOrNull, reflow: enumOf(['constraints', 'reflow']) }, ['reflow']),

  component: object({ id: str, nodes: arrayOf(ref('node')) }, ['id', 'nodes']),

  meta: object({
    title: strOrNull,
    documentType: strOrNull,
    category: strOrNull,
    purpose: strOrNull,
    genome: strOrNull,
    researchVersion: strOrNull,
    createdBy: strOrNull,
  }, []),

  document: object({
    schema: { type: 'string', enum: [SCHEMA_ID] },
    version: { type: 'number', enum: [SCHEMA_VERSION] },
    meta: ref('meta'),
    tokens: ref('tokens'),
    components: { type: 'array', items: closedMap, description: 'Leave empty: the component system is not available yet.' },
    variants: arrayOf(ref('variant')),
    pages: arrayOf(ref('page')),
  }, ['schema', 'version', 'tokens', 'pages']),
};

/** The JSON Schema for one Design JSON V2 document. */
export const DESIGN_DOCUMENT_SCHEMA = Object.freeze({ ...$defs.document, $defs });

/**
 * The response envelope: one JSON object carrying the requested concepts.
 *
 * Strict mode does not support `minItems`/`maxItems`, so the count is stated in
 * the prompt and enforced by the caller, not by the schema.
 *
 * @param {number} count  documents to request (carried in the description).
 * @returns {Object} a strict JSON Schema.
 */
export const buildResponseSchema = (count = 1) => ({
  type: 'object',
  properties: {
    documents: {
      type: 'array',
      description: `Exactly ${count} complete Design JSON V2 document${count === 1 ? '' : 's'}.`,
      items: ref('document'),
    },
  },
  required: ['documents'],
  additionalProperties: false,
  $defs,
});
